import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DropAnimation,
  PointerSensor,
  defaultDropAnimation,
  useSensor,
  useSensors,
  DragStartEvent,
} from '@dnd-kit/core';
import {
  restrictToFirstScrollableAncestor,
  restrictToVerticalAxis,
  restrictToWindowEdges,
} from '@dnd-kit/modifiers';
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
} from '@dnd-kit/sortable';
import React, { FC, useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { FixedSizeList as List } from 'react-window';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { DragHandle, ListItem, SortableElementContainer } from './components';
import { SortableItem } from './sortable-item';

interface Props<T = any> {
  listData: T[];
  onSortChange?: (updatedRow: T, updatedRowOrder: number) => void;
}

const dropAnimation: DropAnimation = {
  ...defaultDropAnimation,
  dragSourceOpacity: 0.5,
};

export const DraggableList: FC<Props> = ({
  listData,
  onSortChange,
}): JSX.Element => {
  const [orderedItems, setOrderedItems] = useState<string[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);

  useDeepCompareEffect(() => {
    if (listData && listData.length > 0) {
      setOrderedItems(listData?.map((x) => x!.key));
    }
  }, [listData]);

  const sensors = useSensors(useSensor(PointerSensor));
  const getIndex = useCallback(
    (_id: string) => orderedItems.indexOf(_id),
    [orderedItems],
  );
  const activeIndex = activeId ? getIndex(activeId) : -1;

  const onDragEnd = useCallback(
    ({ over }: DragEndEvent) => {
      setActiveId(null);
      if (over) {
        const overIndex = getIndex(over.id);

        if (activeIndex !== overIndex && listData) {
          const updatedRow = listData[activeIndex]!;
          const newData = arrayMove<string>(
            orderedItems,
            activeIndex,
            overIndex,
          );

          setOrderedItems(newData);
          onSortChange?.(updatedRow, overIndex);
        }
      }
    },
    [activeIndex, listData, getIndex, onSortChange, orderedItems],
  );

  const onDragStart = useCallback(
    ({ active }: DragStartEvent) => setActiveId(active.id),
    [],
  );

  return (
    <DndContext
      id="draggable_list"
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragCancel={() => setActiveId(null)}
      modifiers={[
        restrictToVerticalAxis,
        restrictToWindowEdges,
        restrictToFirstScrollableAncestor,
      ]}
    >
      <SortableContext items={orderedItems} strategy={rectSortingStrategy}>
        <List
          height={258}
          itemCount={listData.length}
          itemSize={32}
          itemData={listData}
          width="100%"
        >
          {({ index, style }) => (
            <SortableItem
              key={listData[index]?.key}
              id={listData[index]?.key}
              style={style}
              item={listData[index]}
              disabled={!listData[index]?.isVisible}
            />
          )}
        </List>
      </SortableContext>
      {createPortal(
        <DragOverlay dropAnimation={dropAnimation} zIndex={1040}>
          {activeIndex >= 0 && listData[activeIndex] ? (
            <SortableElementContainer isDragOverlay>
              <ListItem>
                {listData[activeIndex]?.render()}
                <DragHandle title={listData[activeIndex]?.title} />
              </ListItem>
            </SortableElementContainer>
          ) : null}
        </DragOverlay>,
        document.body,
      )}
    </DndContext>
  );
};
